import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { IntegrationsService } from '../../../shared/services/integrations.service';
import { ToastService } from '../../../shared/components/toast/toast.service';
import { TestEventModalComponent } from '../../../shared/components/test-event-modal/test-event-modal.component';
import { Integration } from '../../../models/Integration';
import { IntegrationActivity } from '../../../models/IntegrationActivity';

/**
 * SMS-003: Aba "Atividade recente" de uma integração.
 * Lista os últimos N postbacks do buffer Redis com o resultado da decisão do
 * RuleMatcher (SMS-004 popula matched_event/target_lead_list/status/lead_id).
 */
@Component({
  selector: 'app-integration-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss']
})
export class IntegrationActivityComponent implements OnInit, OnDestroy {

  /** Quando usada embutida (ex.: aba dentro de outro componente). */
  @Input() integration: Integration;

  activity: IntegrationActivity[] = [];
  filteredActivity: IntegrationActivity[] = [];
  isLoading = false;

  /**
   * Ação em andamento no botão que a disparou (CU-86akkubzz): envio de teste ou
   * re-processamento. Separada de `isLoading`, que é a lista — antes as duas
   * dividiam a flag e o clique escondia a lista sem dizer qual botão respondia.
   */
  sendingTest = false;
  reprocessingAt: string | null = null;

  get isBusy(): boolean {
    return this.isLoading || this.sendingTest || this.reprocessingAt !== null;
  }

  filterShowErrors = false;
  filterShowTests = false;
  filterSearchText = '';

  private destroy$ = new Subject<void>();

  constructor(
    private integrationsService: IntegrationsService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    if (!this.integration) {
      // Quando renderizada via rota /integrations/:id/activity.
      const id = this.route.snapshot.params.id;
      if (id) {
        // A lista espera a integração; sem isto a tela diria "nenhum evento"
        // enquanto a integração ainda nem chegou.
        this.isLoading = true;
        this.integrationsService.findById(id)
          .pipe(
            catchError(() => of(null as Integration)),
            takeUntil(this.destroy$)
          )
          .subscribe(integration => {
            this.integration = integration;
            if (integration) {
              this.loadActivity();
            } else {
              this.isLoading = false;
            }
          });
        return;
      }
    }

    if (this.integration?.id) {
      this.loadActivity();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadActivity(): void {
    this.isLoading = true;

    this.integrationsService.getRecentActivity(this.integration.id)
      .pipe(
        catchError(() => of({ success: false, data: [] as IntegrationActivity[] })),
        takeUntil(this.destroy$)
      )
      .subscribe((response: any) => {
        this.activity = (response && response.data) ? response.data : [];
        this.applyFilters();
        this.isLoading = false;
      });
  }

  applyFilters(): void {
    this.filteredActivity = this.activity.filter(item => {
      if (this.filterShowErrors && item.status === 'ok') {
        return false;
      }

      if (this.filterShowTests && !item.is_test) {
        return false;
      }

      if (this.filterSearchText) {
        const search = this.filterSearchText.toLowerCase();
        const payloadStr = JSON.stringify(item.payload || {}).toLowerCase();
        const event = (item.matched_event || '').toLowerCase();

        if (!payloadStr.includes(search) && !event.includes(search)) {
          return false;
        }
      }

      return true;
    });
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'ok': return 'bg-success';
      case 'no_match': return 'bg-warning';
      case 'error_mapping':
      case 'error_validation':
      case 'error': return 'bg-danger';
      case 'pending': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'ok': return 'Sucesso';
      case 'no_match': return 'Nenhum evento bateu';
      case 'error_mapping': return 'Erro de mapeamento';
      case 'error_validation': return 'Erro de validação';
      case 'error': return 'Erro';
      case 'pending': return 'Pendente';
      default: return status || 'Desconhecido';
    }
  }

  openTestEventModal(): void {
    const lastPayload = this.activity.length > 0 ? this.activity[0].payload : { lead_id: '123456' };

    const modalRef = this.modalService.open(TestEventModalComponent, { size: 'lg' });
    modalRef.componentInstance.lastPayload = lastPayload;

    modalRef.result.then(
      payload => {
        if (payload) {
          this.sendTestEvent(payload);
        }
      },
      () => { /* descartado */ }
    );
  }

  sendTestEvent(payload: any): void {
    this.sendingTest = true;

    this.integrationsService.testEvent(this.integration.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.success('Evento de teste enviado!');
          setTimeout(() => {
            this.sendingTest = false;
            this.loadActivity();
          }, 1000);
        },
        error: err => {
          this.toastService.error(err?.error?.message || 'Erro ao enviar evento de teste');
          this.sendingTest = false;
        }
      });
  }

  /**
   * Re-processa o último postback do buffer (endpoint reprocess-last).
   * O item exibido na lista é sempre o topo do buffer Redis, então
   * "re-processar este" e "re-processar último" coincidem.
   */
  reprocessItem(item: IntegrationActivity): void {
    if (this.activity.length === 0) {
      this.toastService.error('Nenhum postback disponível para re-processar');
      return;
    }

    this.reprocessingAt = item.received_at;

    this.integrationsService.reprocessLast(this.integration.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.success('Re-processamento iniciado!');
          setTimeout(() => {
            this.reprocessingAt = null;
            this.loadActivity();
          }, 1000);
        },
        error: err => {
          this.toastService.error(err?.error?.message || 'Erro ao re-processar');
          this.reprocessingAt = null;
        }
      });
  }

  formatDate(dateStr: string): string {
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  }

  trackByReceivedAt(_: number, item: IntegrationActivity): string {
    return item.received_at;
  }
}
